import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

function CountryMaster({ token }) {
  const emptyForm = {
    country_name: '',
    is_active: true
  };

  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const loadCountries = async () => {
    try {
      const res = await fetch('/api/v1/core/countries', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to load countries');
        return;
      }
      setCountries(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading countries');
    }
  };

  useEffect(() => {
    loadCountries();
  }, [token]);

  const handleSave = async () => {
    const payload = {
      country_name: (form.country_name || '').trim(),
      is_active: form.is_active
    };
    if (!payload.country_name) {
      setError('Country name is required');
      return;
    }

    try {
      const res = await fetch(editingId ? `/api/v1/core/countries/${editingId}` : '/api/v1/core/countries', {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to save country');
        return;
      }
      setError('');
      setForm(emptyForm);
      setEditingId(null);
      loadCountries();
    } catch {
      setError('Network error while saving country');
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      country_name: row.country_name || '',
      is_active: Boolean(row.is_active)
    });
    setError('');
    scrollToForm();
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`/api/v1/core/countries/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to deactivate country');
        return;
      }
      setError('');
      loadCountries();
    } catch {
      setError('Network error while deleting country');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>Country Master</h2>
      {error && <div className="error-banner">{error}</div>}

      <div className="filter-bar" ref={formRef} style={editHighlightStyle(editHighlight)}>
        <h3>{editingId ? 'Edit Country' : 'Create Country'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          <input
            placeholder="Country Name"
            value={form.country_name}
            onChange={(e) => setForm({ ...form, country_name: e.target.value })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
          <button onClick={handleSave} className="btn-primary" style={{ padding: '0.65rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            {editingId ? 'Update Country' : 'Create Country'}
          </button>
          {editingId && (
            <button onClick={cancelEdit} className="btn-muted" style={{ padding: '0.65rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3>Countries</h3>
      {countries.length === 0 ? (
        <p>No countries found</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Country</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Active</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {countries.map((row) => (
              <tr key={row.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                <td style={{ padding: '0.75rem' }}>{row.country_name}</td>
                <td style={{ padding: '0.75rem' }}>{row.is_active ? 'Yes' : 'No'}</td>
                <td style={{ padding: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => handleEdit(row)} style={{ border: 'none', borderRadius: '4px', padding: '0.45rem 0.7rem', background: '#3498db', color: '#fff', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => handleDelete(row.id)} className="btn-danger" style={{ border: 'none', borderRadius: '4px', padding: '0.45rem 0.7rem', cursor: 'pointer' }}>Deactivate</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default CountryMaster;
